import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { HomePage } from "./pages/HomePage";
import { NewRetroPage } from "./pages/NewRetroPage";
import { BoardPage } from "./pages/BoardPage";
import { LoginPage } from "./pages/LoginPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { ThemeToggle } from "./components/ThemeToggle";
import { LanguagePicker } from "./components/LanguagePicker";
import { useTranslation } from "./i18n";
import { useAuth } from "./hooks/useAuth";

function BrandMark() {
  return (
    <span className="relative flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-nm-primary to-nm-accent shadow-subtle">
      <span aria-hidden="true" className="text-lg font-bold leading-none text-white">
        ∞
      </span>
    </span>
  );
}

function Avatar({ label, tone }: { label: string; tone: "primary" | "accent" }) {
  const styles =
    tone === "primary"
      ? "bg-nm-primary/12 text-nm-primary"
      : "bg-nm-accent/12 text-nm-accent";

  return (
    <span
      aria-hidden="true"
      className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${styles}`}
    >
      {label.charAt(0).toUpperCase()}
    </span>
  );
}

function App() {
  const { auth, logout } = useAuth();
  const { t } = useTranslation();

  return (
    <BrowserRouter>
      <div className="flex min-h-screen flex-col">
        <header className="sticky top-0 z-50 border-b border-line/80 bg-canvas/70 backdrop-blur-xl">
          <nav className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-3 sm:px-6">
            <Link
              to="/"
              className="flex items-center gap-2.5 rounded-lg transition-opacity hover:opacity-80"
            >
              <BrandMark />
              <span className="hidden text-lg font-bold tracking-tight text-ink sm:inline">
                {t("common.appName")}
              </span>
            </Link>

            <div className="flex items-center gap-1.5">
              {auth.staff && (
                <>
                  <span className="flex items-center gap-2 pr-1">
                    <Avatar label={auth.staff.username} tone="primary" />
                    <span className="hidden max-w-[16rem] truncate text-sm font-medium text-ink-soft md:inline">
                      {auth.staff.username}
                    </span>
                  </span>
                  <LanguagePicker />
                  <ThemeToggle />
                  <button
                    onClick={logout}
                    className="rounded-lg px-3 py-1.5 text-sm font-semibold text-ink-muted transition-colors hover:bg-surface-sunken hover:text-ink"
                  >
                    {t("nav.logOut")}
                  </button>
                </>
              )}

              {auth.participant && !auth.staff && (
                <>
                  <span className="flex items-center gap-2 pr-1">
                    <Avatar label={auth.participant.displayName} tone="accent" />
                    <span className="hidden text-sm font-medium text-ink-soft sm:inline">
                      {auth.participant.displayName}
                    </span>
                  </span>
                  <LanguagePicker />
                  <ThemeToggle />
                </>
              )}

              {!auth.staff && !auth.participant && (
                <>
                  <LanguagePicker />
                  <ThemeToggle />
                </>
              )}
            </div>
          </nav>
        </header>

        <main className="flex-1">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/new" element={<NewRetroPage />} />
            <Route path="/r/:publicId" element={<BoardPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </main>

        <footer className="mt-16 border-t border-line py-6">
          <div className="mx-auto max-w-7xl px-6 text-center">
            <p className="text-xs text-ink-faint">
              {t("common.appName")} · {t("common.poweredBy")}{" "}
              <a
                href="https://neuromobile.es"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-ink-muted transition-colors hover:text-nm-accent"
              >
                Neuromobile
              </a>
            </p>
          </div>
        </footer>
      </div>
    </BrowserRouter>
  );
}

export default App;
